import { apiClient } from "@rti/shared";
import { trackGoogleAnalyticsEvent } from "./googleAnalytics";

export const IMPERSONATED_USER_KEY = "impersonated_user";

export const isImpersonatingSession = (): boolean => {
  try {
    return !!localStorage.getItem(IMPERSONATED_USER_KEY);
  } catch {
    return false;
  }
};

declare global {
  interface Window {
    clarity?: (...args: any[]) => void;
    _hsq?: any[];
  }
}

/**
 * Universal user event tracker.
 * Supports multiple analytics providers (HubSpot, Clarity, etc.)
 * Safe to call anywhere — respects environment and SSR.
 */
export const trackUserEvent = async (
  id: string,
  value?: string | number,
  properties: Record<string, any> = {}
) => {
  if (typeof window === "undefined") return; // SSR safety
  if (isImpersonatingSession()) return;

  const trackingEnabled =
    ((import.meta as any).env?.VITE_HUBSPOT_TRACKING_ENABLED as string | undefined) === "true";

  // HubSpot tracking block
  if (trackingEnabled) {
    window._hsq = window._hsq || [];

    // Construct pseudo-path for the event
    const params = new URLSearchParams();
    if (typeof value !== "undefined") {
      params.set("value", String(value));
    }
    for (const [key, val] of Object.entries(properties || {})) {
      if (typeof val !== "undefined") {
        params.set(key, String(val));
      }
    }
    const pseudoPath = `/event/${encodeURIComponent(id)}${params.toString() ? "?" + params.toString() : ""}`;

    if (typeof document !== "undefined") {
      if (value) {
        document.title = `${value} | ResinSmart User Action`;
      } else {
        document.title = "ResinSmart";
      }
    }

    window._hsq.push(["setPath", pseudoPath]);
    window._hsq.push(["trackPageView"]);

  }

  trackGoogleAnalyticsEvent(id, properties);

  try {
    const clarity = (window as any).clarity;
    if (typeof clarity === 'function') {
      clarity('event', id);
      if (value !== undefined) {
        clarity('set', `${id}_value`, String(value));
      }
      for (const [key, val] of Object.entries(properties)) {
        if (val !== undefined) {
          clarity('set', `${id}_${key}`, String(val));
        }
      }
    }
  } catch (clarityError) {
    console.warn('[RS] Clarity tracking error:', clarityError);
  }

  // Always post user activity to the backend, regardless of HubSpot tracking
  try {
    let user_id = 0;
    try {
      user_id = (window as any).currentUser?.user_id ?? 0;
    } catch (e) {
      // Defensive: missing window.currentUser should not break tracking
      user_id = 0;
    }
    const url = window.location.pathname;
    const title = typeof value !== "undefined" ? String(value) : "";
    const timestamp = Date.now();
    const metadata = properties;
    await apiClient.postUserActivity({
      user_id,
      event: id,
      url,
      title,
      timestamp,
      metadata,
    });
  } catch (err) {
    console.error("[RS] Failed to post user activity to backend", err);
  }
};
